import {action, computed, observable} from 'mobx';
import {MagicMarkerOptions} from '@techsee/techsee-media-service-client';
import {IContainerPoint} from '@techsee/techsee-media-service-client/lib/components/MagicMarker/MagicMarkerComponent';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {EventEmitter} from 'events';
import {IChatApi} from '../../../../services/ts-chat-api/ChatApi';
import {SessionClientRole} from '@techsee/techsee-media-service/lib/MediaConstants';

export class MagicMarkerController extends EventEmitter {
    @observable public remoteDrawings: IContainerPoint[] = [];
    @observable private videoDimensions: {width: number; height: number} = {width: 640, height: 480};
    @observable private enabled = false;
    @observable private isMirrored = false;
    @observable private isFullScreen = false;

    constructor(
        private tsChatApi: IChatApi,
        public readonly isMobile: boolean,
        private readonly sendEventLog: (eventType: string, meta?: Record<string, unknown>) => Promise<void>
    ) {
        super();
        this.setupEvents();
    }

    private setupEvents = () => {
        this.tsChatApi.on('clientDrawings', (propName: string, value: IContainerPoint[]) => {
            if (value.length) {
                this.emit('magicMarkerReceived');

                this.sendEventLog(LOG_EVENTS.magicMarkerDrawingReceived);
            }

            this.updateRemoteDrawings(value);
        });

        this.tsChatApi.on('dashboardDrawings', (propName: string, value: IContainerPoint[]) => {
            if (value.length) {
                this.sendEventLog(LOG_EVENTS.magicMarkerDrawingReceived);
            }

            this.updateRemoteDrawings(value);
        });
    };

    @action
    private updateRemoteDrawings = (drawings: IContainerPoint[]) => {
        this.remoteDrawings = drawings;
    };

    @action
    public clearRemoteDrawings = () => {
        this.remoteDrawings = [];
    };

    public sendDrawings = (drawings: IContainerPoint[]) => {
        this.tsChatApi.setStatusFast('drawings', drawings, true);

        if (drawings.length) {
            this.emit('magicMarkerSent');

            this.sendEventLog(LOG_EVENTS.magicMarkerDrawingSent);
        }
    };

    @action
    public setVideoDimensions = (dimensions: {width: number; height: number}) => {
        if (this.videoDimensions.width === dimensions.width && this.videoDimensions.height === dimensions.height) {
            return;
        }

        this.videoDimensions = {...dimensions};
    };

    @action
    public setEnabled = (enabled: boolean) => {
        this.enabled = enabled;
    };

    @action
    public setMirrored = (isMirrored: boolean) => {
        this.isMirrored = isMirrored;
    };

    @action
    public setIsFullScreen = (isFullScreen: boolean) => {
        this.isFullScreen = isFullScreen;
    };

    @computed
    public get options(): MagicMarkerOptions {
        return {
            drawingOptions: {
                lineWidth: this.tsChatApi.accountSettings?.magicMarker?.dashboard.lineWidth,
                lineWidthRefResolution: this.videoDimensions,
                strokeColorRGB: this.tsChatApi.accountSettings?.magicMarker?.dashboard.strokeColorRgb
            },
            timeUntilDrawingStartFading:
                this.tsChatApi.accountSettings?.magicMarker?.dashboard.timeUntilDrawingStartFading,
            drawingOptionsRemote: {
                lineWidth: this.tsChatApi.accountSettings?.magicMarker?.client.lineWidth,
                lineWidthRefResolution: this.videoDimensions,
                strokeColorRGB: this.tsChatApi.accountSettings?.magicMarker?.client.strokeColorRgb
            },
            timeUntilDrawingStartFadingRemote:
                this.tsChatApi.accountSettings?.magicMarker?.client.timeUntilDrawingStartFading,
            isMobile: this.isMobile,
            canDraw: this.tsChatApi.accountSettings?.magicMarker?.dashboard.enable && !this.tsChatApi.observer,
            isMirrored: this.isMirrored,
            isFullScreen: this.isFullScreen as boolean
        };
    }

    @computed
    public get shouldDisplayComponent() {
        return (
            this.tsChatApi.accountSettings?.magicMarker?.enabledInFacemeet &&
            this.tsChatApi.accountSettings?.magicMarker?.dashboard.enable &&
            this.enabled
        );
    }
}
